import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Tenant } from './tenant';
export { Tenant };
import { tenantSchema } from '@schemas/business-objects/tenant';
import { ActivityMappingFormArray } from './activity-mapping-form-group';
import { ConditionWatcherFormArray } from './condition-watcher-form-group';
import { LatestProcessDesignFormArray } from './latest-process-design-form-group';
import { MessageListenerFormArray } from './message-listener-form-group';
import { MyUserTaskFormArray } from './my-user-task-form-group';
import { PendingStateUpdateFormArray } from './pending-state-update-form-group';
import { ProcessDesignFormArray } from './process-design-form-group';
import { ProcessDesignActivityCountFormArray } from './process-design-activity-count-form-group';
import { ProcessDesignHistoryFormArray } from './process-design-history-form-group';
import { ProcessInstanceFormArray } from './process-instance-form-group';
import { ProcessInstanceHistoryFormArray } from './process-instance-history-form-group';
import { ProcessInstanceSummaryFormArray } from './process-instance-summary-form-group';
import { ServiceTaskFormArray } from './service-task-form-group';
import { ServiceTaskHistoryFormArray } from './service-task-history-form-group';
import { TenantAssignmentFormArray } from './tenant-assignment-form-group';
import { TimerFormArray } from './timer-form-group';
import { TokenFormArray } from './token-form-group';
import { TokenHistoryFormArray } from './token-history-form-group';
import { TokenLinkFormArray } from './token-link-form-group';
import { UserTaskFormArray } from './user-task-form-group';
import { UserTaskHistoryFormArray } from './user-task-history-form-group';
import type { TenantConfig } from '../interface-definitions/index';

export class TenantFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    description: SchemaFormControl;
    tenantAssignments: TenantAssignmentFormArray;
    processDesigns: ProcessDesignFormArray;
    processDesignHistories: ProcessDesignHistoryFormArray;
    processInstances: ProcessInstanceFormArray;
    processInstanceHistories: ProcessInstanceHistoryFormArray;
    pendingStateUpdates: PendingStateUpdateFormArray;
    tokens: TokenFormArray;
    tokenHistories: TokenHistoryFormArray;
    tokenLinks: TokenLinkFormArray;
    userTasks: UserTaskFormArray;
    userTaskHistories: UserTaskHistoryFormArray;
    serviceTasks: ServiceTaskFormArray;
    serviceTaskHistories: ServiceTaskHistoryFormArray;
    timers: TimerFormArray;
    messageListeners: MessageListenerFormArray;
    conditionWatchers: ConditionWatcherFormArray;
    activityMappings: ActivityMappingFormArray;
    latestProcessDesigns: LatestProcessDesignFormArray;
    processDesignActivityCounts: ProcessDesignActivityCountFormArray;
    myUserTasks: MyUserTaskFormArray;
    processInstanceSummaries: ProcessInstanceSummaryFormArray;
  };

  declare value: Partial<Tenant>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(tenantSchema, Tenant, data, options);
  }

  get object(): Tenant {
    return new Tenant(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'tenantAssignments':
        return new TenantAssignmentFormArray();
      case 'processDesigns':
        return new ProcessDesignFormArray();
      case 'processDesignHistories':
        return new ProcessDesignHistoryFormArray();
      case 'processInstances':
        return new ProcessInstanceFormArray();
      case 'processInstanceHistories':
        return new ProcessInstanceHistoryFormArray();
      case 'pendingStateUpdates':
        return new PendingStateUpdateFormArray();
      case 'tokens':
        return new TokenFormArray();
      case 'tokenHistories':
        return new TokenHistoryFormArray();
      case 'tokenLinks':
        return new TokenLinkFormArray();
      case 'userTasks':
        return new UserTaskFormArray();
      case 'userTaskHistories':
        return new UserTaskHistoryFormArray();
      case 'serviceTasks':
        return new ServiceTaskFormArray();
      case 'serviceTaskHistories':
        return new ServiceTaskHistoryFormArray();
      case 'timers':
        return new TimerFormArray();
      case 'messageListeners':
        return new MessageListenerFormArray();
      case 'conditionWatchers':
        return new ConditionWatcherFormArray();
      case 'activityMappings':
        return new ActivityMappingFormArray();
      case 'latestProcessDesigns':
        return new LatestProcessDesignFormArray();
      case 'processDesignActivityCounts':
        return new ProcessDesignActivityCountFormArray();
      case 'myUserTasks':
        return new MyUserTaskFormArray();
      case 'processInstanceSummaries':
        return new ProcessInstanceSummaryFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Tenant[]> {
    return Tenant.find(filter);
  }

  static async findOne(filter?: any): Promise<Tenant | null> {
    return Tenant.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<Tenant> {
    return Tenant.findById(id, filter);
  }

  static async create(data: Partial<Tenant>): Promise<Tenant> {
    return Tenant.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<Tenant>,
  ): Promise<Tenant> {
    return Tenant.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return Tenant.deleteById(id);
  }

  static async currentUser(filter: any): Promise<any[]> {
    return Tenant.currentUser(filter);
  }
}

export class TenantFormArray extends PersistedFormArray {
  declare controls: TenantFormGroup[];

  readonly entityName = 'Tenant' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(tenantSchema, Tenant, options);
  }

  protected override createItemGroup() {
    return new TenantFormGroup();
  }

  get array(): Tenant[] {
    return this.controls.map((group: TenantFormGroup) => group.object);
  }
}

export class TenantPersistedArray extends PersistedArray<Tenant> {
  readonly entityName = 'Tenant' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Tenant, options);
  }
}
